import cx from 'classnames';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, useState } from 'react';

import useIsMobile from '../../../hooks/use-is-mobile';
import useLoggedUser from '../../../hooks/use-logged-user';
import { useAppContext } from '../../providers/app-context';
import IconTruckFilled from '../../ui/icons/truck-filled';
import BPImage from '../../ui/v2/image';
import NoImage from '../../ui/no-image-placeholder';
import VehiclePreview from '../../ui/v2/vehicle-preview';

import classes from './products.module.css';
import IconCarDoor from '../../ui/icons/car-door';
import IconCarEngine from '../../ui/icons/car-engine';
import IconCarKm from '../../ui/icons/car-km';
import Button from '../../ui/button';
import IconCircleIHover from '../../ui/icons/circle-i-hover';
import Tooltip from '../../ui/v2/tooltip';
import IconClose from '../../ui/icons/close-new-version';
import IconCloseHover from '../../ui/icons/close-hover';
import { useRouter } from 'next/router';
import { useAtom } from 'jotai';
import Cookies from 'js-cookie';
import { viewSelection } from '../../../state/selection';
import IconCheck from '../../ui/icons/check';
import IconACTWhite2 from '../../ui/icons/act-white-2';
import { getExpressshipping } from '../../../utils/get-express-shipping';
import ImageSlider from '../../ui/image-group/v2/image-slider';
import IconGallery from '../../ui/icons/gallery';
import IconSearch from '../../ui/icons/search';
import { availabilityPoint, isATCDisabled } from './list-card';

const ColumnCard: FunctionComponent<{
  item: TProductListItem;
  actState: 'ADDED' | undefined;
  addToCartMutate: any;
  view?: 'MOBILE' | 'DESKTOP'
  availability?: any;
  shipping?: any;
}> = ({
  item: {
    id,
    sku,
    main_image,
    part,
    car_logo_url,
    car_logo_white_url,
    car_brand,
    car_model,
    car_version,
    car_door_nr,
    car_date,
    engine_code,
    mileage,
    reference,
    pricing,
    delivery_day_start,
    delivery_day_end,
    steering_wheel_side,
    images,
    milageCoUK,
    listWithImages,
    expedition_date,
  },
  actState,
  addToCartMutate,
  view,
  availability,
  shipping
}) => {
    const [showMoreModal, setShowMoreModal] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);

    const { detailedPrice: hasDetailedPrice } = useAppContext();
    const { locale = 'en' } = useRouter();
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const user = useLoggedUser();
    const isResaleer = user?.staff.company_type == 'supplier';
    const isUkWebsite = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');
    const isProUser = user?.staff.is_pro;
    const isATCDisabledButton = isATCDisabled(availability, isProUser || false)
    const specificAvailability = availabilityPoint(availability, isProUser, shipping, t);

    return (
      <section
        className={cx(
          'gtm_product_impressions',
          classes.productCardMobile,
          'relative',
          'grid max-lg:gap-2 p-2 group hover:bg-brand-10',
          'has-[:hover]:bg-white group-has-[:hover]:bg-white',
          {
            'gap-2 shadow-md': view === 'MOBILE' || isMobile,
          }
        )}
      >
        <section className={cx('relative', 'bg-neutral-10 group/images')}>
          {listWithImages ? (
            <>
              <ImageSlider list={listWithImages} cardView={'COLUMN'} />
              <div className="absolute py-[2px] px-1 bg-netrual-80 bottom-2 left-2 text-xs font-bold text-white rounded-sm flex gap-1 items-center justify-center">
                <IconGallery className="w-3 h-3"></IconGallery>
                {listWithImages.length}
              </div>
            </>
          ) : main_image ? (
            <BPImage
              src={main_image}
              layout="fill"
              objectFit="cover"
              alt={`${part} ${car_brand} ${car_model}${car_version && ` ${car_version}`
                }${reference && ` ${reference}`}`}
              draggable={false}
            />
          ) : (
            <NoImage color="text-neutral-30" />
          )}

          {getExpressshipping(delivery_day_start, delivery_day_end) && (
            <div className="absolute p-1 bg-cta-100 top-2 left-2 text-xs font-bold text-white rounded-sm">
              {t('product:expressShipping')}
            </div>
          )}
          {((steering_wheel_side && steering_wheel_side === 'R') ||
            isUkWebsite) && (
              <div className="absolute p-1 bg-neutral-80 top-2 right-2 text-xs font-bold text-white rounded-sm">
                {steering_wheel_side && steering_wheel_side === 'R'
                  ? t('product:wheelSide.R')
                  : t('product:wheelSide.L')}
              </div>
            )}
        </section>
        <section
          className={cx('grid', 'gap-4 max-lg:px-2', {
            'px-2': view === 'MOBILE',
          })}
        >
          <div>
            <div className={cx('text-neutral-50', 'text-xs', 'font-bold pb-1')}>
              {sku}
            </div>
            <div className={cx('font-bold', 'text-base')}>{part}</div>
            <div className={cx('font-bold', 'text-base')}>
              <VehiclePreview
                sizeLogo="small"
                sizeText="small"
                textBold={false}
                highlightDoors={false}
                overflowText={true}
                info={{
                  brand: {
                    logo_url: isMobile ? car_logo_white_url : car_logo_url,
                    name: car_brand,
                  },
                  model: { name: car_model },
                  ...(car_version && { version: { name: car_version } }),
                  years: car_date,
                }}
              />
            </div>
          </div>
          <section className="flex flex-col gap-4 justify-end truncate">
            <section className="flex flex-row gap-4 justify-start items-center">
              <div className="flex flex-row gap-[6px] justify-center items-center">
                <IconCarDoor className="w-[12px] h-[13px] text-brand-100"></IconCarDoor>
                <span className="text-xs truncate">{car_door_nr}</span>
              </div>
              <div className="flex flex-row gap-[6px] justify-center items-center ">
                <IconCarEngine className="w-[16px] text-brand-100"></IconCarEngine>
                <span className="text-xs truncate">{engine_code || '-'}</span>
              </div>
              <div className="flex flex-row gap-[6px] justify-center items-center">
                <IconCarKm className="w-[15px] text-brand-100"></IconCarKm>
                <span className="text-xs truncate">
                  {milageCoUK && milageCoUK > 0
                    ? milageCoUK.toFixed(0)
                    : !mileage || parseInt(mileage, 10) === 0
                      ? '-'
                      : mileage}
                </span>
              </div>
            </section>
            <div className={cx('flex flex-row gap-[6px] h-4', 'text-xs')}>
              {
                reference && <><div
                  className={cx('truncate')}
                  ref={(el) => {
                    if (el?.offsetWidth < el?.scrollWidth) {
                      //ellipsis was added
                      setShowMoreModal(true);
                    } else {
                      setShowMoreModal(false);
                    }
                  }}
                >
                  {reference || ''}
                </div>
                  <div className="relative flex !items-end !justify-end">
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenModal(true);
                      }}
                      isFullWidth={false}
                      disableHeight={true}
                      className={cx(
                        { hidden: !showMoreModal },
                        '!bg-none',
                        'w-fit !px-0 group'
                      )}
                    >
                      <IconCircleIHover className={cx('w-4 h-4 text-black')} />
                    </Button>
                  </div></>
              }
            </div>
          </section>
          {specificAvailability &&
            <div className='flex justify-end items-start text-right'>
              {specificAvailability}
            </div>}
        </section>
        <section
          className={cx(
            'flex flex-row gap-4 pr-2 w-full justify-between relative h-fit'
          )}
        >
          {openModal && (
            <Tooltip className={'!-top-[40px] !-right-2'}>
              <section className={cx('flex flex-col gap-2 text-white')}>
                <div>
                  <p className="text-base pr-4">{reference || '-'}</p>
                </div>
                <Button
                  isFullWidth={false}
                  className="absolute top-0 right-0 h-12 w-12 text-center group"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenModal(false);
                  }}
                  data-cy="close-modal"
                  variant="NO-HOVER"
                >
                  <IconClose className={cx('w-4 h-4', 'group-hover:hidden')} />
                  <IconCloseHover
                    className={cx('w-4 h-4', 'hidden group-hover:block')}
                  />
                </Button>
              </section>
            </Tooltip>
          )}
          <div className={cx('flex justify-end items-end pl-2')}>
            {specificAvailability ?
              <></> :
              <Button
                variant="ROUNDED-CTA"
                isFullWidth={false}
                disableHeight={false}
                disabled={!pricing || actState === 'ADDED' || isATCDisabledButton}
                className={cx(
                  'group/act',
                  {
                    '!bg-cta-100': actState === 'ADDED',
                  },
                  {
                    'hover:shadow-lg': !(!pricing || actState === 'ADDED'),
                    'hover:!cursor-not-allowed': (isATCDisabledButton === true)
                  }
                )}
                onClick={(e) => {
                  e.preventDefault();
                  addToCartMutate({
                    locale,
                    product_id: id,
                    csrftoken: Cookies.get('csrftoken') || '',
                    is_advanced_view: false,
                  });
                }}
              >
                {actState === 'ADDED' ? (
                  <IconCheck
                    className={cx(
                      'h-4',
                      'w-4 text-white group-hover/act:text-neutral-80'
                    )}
                  ></IconCheck>
                ) : (
                  <IconACTWhite2
                    className={cx(
                      'h-4',
                      'w-4 text-white iconv2 group-hover/act:text-neutral-80'
                    )}
                  ></IconACTWhite2>
                )}
              </Button>}
          </div>
          <div className="flex flex-col gap-4">
            <div className={cx('text-right', 'grid', 'grid-rows-1', 'items-end')}>
              {pricing ? (
                <>
                  <div className={cx('font-bold', 'text-base')}>
                    {hasDetailedPrice &&
                      pricing.details?.product_price_in_currency_printable
                      ? pricing.details?.product_price_in_currency_printable
                      : pricing.price_final_printable}
                  </div>
                  <div
                    className={cx(
                      'font-bold',
                      'text-xs',
                      '',
                      isResaleer ? 'text-red-100' : 'text-neutral-50'
                    )}
                  >
                    {isResaleer && (
                      <>
                        {t('product:resale_price')}
                        <span className="text-neutral-50"> | </span>
                      </>
                    )}
                    <span className="text-neutral-50">
                      {hasDetailedPrice &&
                        pricing.details?.shipping_in_currency_printable ? (
                        <Trans
                          i18nKey={`product:checklist.tax_included_if_applicable`}
                          components={{ b: <b /> }}
                        />
                      ) : (
                        <Trans
                          i18nKey={`product:checklist.vat_shipping_${pricing.price_message}`}
                          components={{ b: <b /> }}
                        />
                      )}
                    </span>
                  </div>
                  {hasDetailedPrice &&
                    pricing.details?.shipping_in_currency_printable && (
                      <div
                        className={cx('flex flex-col-reverse mt-1', 'font-bold')}
                      >
                        <span className="text-neutral-50 text-xs inline-block">
                          {t('products:detail-price.shipping-price')}
                        </span>
                        <span className={cx('block text-base')}>
                          {pricing.details?.shipping_in_currency_printable}
                        </span>
                      </div>
                    )}
                </>
              ) : (
                <>
                  <div className={cx('font-bold', 'text-base')}>
                    {t('product:price_on_request')}
                  </div>
                  <div className={cx('font-bold', 'text-xs', 'text-neutral-50')}>
                    {t('product:ask_quote')}
                  </div>
                  {hasDetailedPrice && <div className={cx('h-[44px]')}></div>}
                </>
              )}
            </div>
            {delivery_day_start && delivery_day_end && !expedition_date ? (
              <div
                className={cx(
                  'text-xs',
                  'text-right',
                  'flex',
                  'items-center',
                  'justify-end'
                )}
              >
                <Trans
                  key="product:checklist.delivery-short"
                  i18nKey="product:checklist.delivery-short"
                  components={{ b: <b /> }}
                  values={{
                    from: delivery_day_start,
                    to: delivery_day_end,
                  }}
                />
                <IconTruckFilled
                  className={cx('lg:inline-block text-brand-100', 'h-4', 'ml-1')}
                />
              </div>
            ) : expedition_date ? (
              <div className={cx(
                'text-xs',
                'text-right',
                'flex',
                'items-center',
                'justify-end'
              )}>
                <div className={cx(
                  'text-xs',
                  'text-right',
                  'flex flex-col',
                )}>
                  <p><Trans
                    i18nKey="product:checklist.expedition_date_short"
                    components={{ b: <b />, br: <br /> }}
                    values={{
                      expedition_date: expedition_date,
                      delivery_day_start: delivery_day_start || '1',
                      delivery_day_end: delivery_day_end || '3',
                    }}
                  /></p>
                  <p> <Trans
                    i18nKey="product:checklist.expedition_date_short_2"
                    components={{ b: <b />, br: <br /> }}
                    values={{
                      expedition_date: expedition_date,
                      delivery_day_start: delivery_day_start || '1',
                      delivery_day_end: delivery_day_end || '3',
                    }}
                  /></p>
                </div>
                <IconTruckFilled
                  className={cx(
                    'lg:inline-block text-brand-100',
                    'h-4',
                    'ml-1'
                  )}
                />
              </div>
            ) : <div />}
          </div>
        </section>
      </section >
    );
  };

export default ColumnCard;
